/* eslint-disable  */
import {
	useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import { useDispatch } from 'react-redux';
import {
	BarChart3,
	Filter, SquareArrowOutUpRight, WorkflowIcon, RefreshCcw, Loader2, Box, Pencil, Maximize2, Minimize2, X, ChevronDown, ChevronUp, ArrowUp
} from 'lucide-react';
import { BackdropLoader } from '@/Components/Loaders';
import { Button } from '@/Components/ui/button';
import { urls } from '@/Constants';
import GlobalSearch from '@/Components/globalSearch/search';
import drawerActions from '@/Actions/drawer.actions';
import aiPackaging from '@/Services/aiPackaging.services';
import { filtersInfoChip, removeKeyFromUrl, unit_map, useQuery } from '@/Helpers';
import { dashboardTabs, parentTabs, operationalDataTabs, orderSourceTabs, ORDER_SOURCE_PARENT_TABS } from './constants';
import TableWithTabs from '../AIFMSUMMARY/TableWithTabs';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import { useTableState } from './hooks/useTableState';
import { useAiFmsData } from './hooks/useAiFmsData';
import { useTableData } from './hooks/useTableData';
import moment from 'moment';
import StatsComp from '../ProductionAnalysis/StatsComp';
import DatePickerCustom from '@/Components/DatePickerCustom/DatePickerCustom';
import BatchPicker from '@/Components/BatchPicker/BatchPicker';
import { useAuth } from '@/Components/Auth/Login/auth';
import { modalActions } from '@/Actions';
import CustomModal from '@/Components/Modals/CustomModal/CustomModal';
import BatchPerformanceModalContent from '@/Components/Modals/BatchPerformanceModal/BatchPerformanceModalContent';
import BatchPerformanceHandler from './Component/BatchPerformanceHandler';
import noMachineIcon from '@/Images/noweigh.svg';
import RequestOperationDataModal from './Component/RequestOperationDataModal';
import { Database } from 'lucide-react';

const exportInfo = {
	showExportBtn: true,
};

const LoadingSpinner = (isLoading) => (
	<BackdropLoader isLoading={isLoading} showProgress={isLoading} />
);

const extractUnit = (valueString) => {
	if (!valueString || typeof valueString !== 'string') return '';
	const match = valueString.match(/[\d.]+\s*([a-zA-Z]+)/);
	return match ? match[1] : '';
};

// Helper to format number to 3 decimal places, returns empty string if invalid
const formatNum = (value, decimals = 3) => {
	const num = parseFloat(value);
	return isNaN(num) ? '' : num.toFixed(decimals);
};

// Helper to calculate shortfall (ordered - fulfilled) if positive
const calcShortfall = (ordered, fulfilled, isFixed = true) => {
	const o = parseFloat(ordered) || 0;
	const f = parseFloat(fulfilled) || 0;
	return o > f ? (isFixed ? (o - f).toFixed(3) : o - f) : '';
};

const dateTypeArr = [
	{
		label: 'Scheduled At',
		value: 'scheduledAt',
	},
];

const PAIR_SEPARATOR = ' · ';
const pairCardValue = (card) => {
	const amount = card?.amount;
	if (typeof amount !== 'string' || !String(card?.label || '').startsWith('Dispatch · Receive') || !amount.includes(PAIR_SEPARATOR)) {
		return amount;
	}
	const [dispatchValue, receiveValue] = amount.split(PAIR_SEPARATOR);
	const side = (tag, value) => (
		<span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.1 }}>
			<span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', opacity: 0.65 }}>{tag}</span>
			<span>{value}</span>
		</span>
	);
	return (
		<span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: '12px' }}>
			{side('DISPATCH', dispatchValue)}
			<span style={{ alignSelf: 'stretch', width: '1px', background: 'currentColor', opacity: 0.3 }} />
			{side('RECEIVE', receiveValue)}
		</span>
	);
};

function StatsSection({ statsCards, isStatsLoading, statsCacheTime, cacheTime, highlightStatsCache, onRefreshStats }) {
	const showStatsCacheTime = statsCacheTime && (() => {
		if (!cacheTime || statsCacheTime === cacheTime) return false;
		const fmt = 'DD-MMM-YYYY hh:mm:ss A';
		const diff = Math.abs(moment(statsCacheTime, fmt).diff(moment(cacheTime, fmt), 'seconds'));
		return diff > 2;
	})();
	return (
		<div className='relative'>
			<StatsComp statsCards={statsCards} />
			{isStatsLoading && (
				<div className='absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg backdrop-blur-[1px]'>
					<Loader2 size={20} className='animate-spin text-yellow-500' />
				</div>
			)}
			{showStatsCacheTime && (
				<span
					onClick={() => { if (!isStatsLoading) onRefreshStats(); }}
					className={`absolute bottom-6 right-0 z-10 flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold shadow-sm transition-all origin-center
						${isStatsLoading
							? 'cursor-not-allowed opacity-50 bg-orange-50 border-orange-200 text-orange-600'
							: 'cursor-pointer'
						}
						${highlightStatsCache
							? 'bg-orange-200 border-orange-400 text-orange-800 ring-1 ring-orange-100 shadow-md'
							: 'bg-orange-100 border-orange-200 text-orange-700 hover:bg-orange-200'
						}`}
				>
					{isStatsLoading ? <Loader2 size={10} className='animate-spin' /> : <RefreshCcw size={10} />}
					Last Synced {statsCacheTime}
				</span>
			)}
		</div>
	);
}

function AiFmsDynamicPage() {
	const dispatch = useDispatch();
	const { appConfig = {} } = useAuth();
	const isNagaCustomer = appConfig?.customerId === 72;
	const isDanaChogaCustomer = appConfig?.customerId === 70;
	const params = useQuery();
	const parentValue = params.get('parentValue');
	const [filters, setFilters] = useState({});
	const [search, setSearch] = useState({});
	const [overrideDateRange, setOverrideDateRange] = useState({ from: null, to: null });
	const [selectedBatches, setSelectedBatches] = useState([]);
	const [isBatchesCollapsed, setIsBatchesCollapsed] = useState(false);
	const [isBatchPerformanceActive, setIsBatchPerformanceActive] = useState(false);
	const [batchModalShiftId, setBatchModalShiftId] = useState(null);
	useEffect(() => {
		if ((selectedBatches?.length ?? 0) === 0) {
			setBatchModalShiftId(null);
		}
	}, [selectedBatches?.length]);
	const skipFetchRef = useRef(false);
	const tableWithTabsRef = useRef(null);
	const [adjustLeftOver, setAdjustLeftOver] = useState(true);
	const adjustLeftOverRef = useRef(true);
	adjustLeftOverRef.current = adjustLeftOver;

	// Planned / Adhoc / All split on the Planned-vs-Actual dashboard. Defaults to
	// 'combined' (the pre-existing behaviour); only sent to the backend when it is
	// something else, so the untouched case keeps hitting the same cache rows.
	const [orderSource, setOrderSource] = useState('combined');
	const orderSourceRef = useRef('combined');
	orderSourceRef.current = orderSource;

	const [isFullScreen, setIsFullScreen] = useState(false);
	const [highlightCache, setHighlightCache] = useState(false);
	const [highlightStatsCache, setHighlightStatsCache] = useState(false);
	// "Request Data" modal — shown via empty-state CTA when Process Operational
	// Data view returns no results (we no longer fall back to dummy in that mode).
	const [isRequestDataModalOpen, setIsRequestDataModalOpen] = useState(false);
	useEffect(() => {
		if (!isFullScreen) return;
		const onKeyDown = (e) => { if (e.key === 'Escape') setIsFullScreen(false); };
		window.addEventListener('keydown', onKeyDown);
		// Lock body scroll while overlay is open
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			window.removeEventListener('keydown', onKeyDown);
			document.body.style.overflow = prevOverflow;
		};
	}, [isFullScreen]);

	const scrollContainerRef = useRef(null);
	const [showScrollTop, setShowScrollTop] = useState(false);
	useEffect(() => {
		const onScroll = (e) => {
			const target = e.target === document ? document.scrollingElement : e.target;
			if (!target || typeof target.scrollTop !== 'number') return;
			// Only containers that hold the table (page / full-screen overlay) — not dropdowns
			// or the table's own horizontal scroll.
			if (target.scrollHeight <= target.clientHeight) return;
			// The table's own scroll box (rows under the sticky header) counts too.
			const tableEl = tableWithTabsRef?.current;
			if (tableEl && !target.contains(tableEl) && !tableEl.contains(target)) return;
			scrollContainerRef.current = target;
			setShowScrollTop(target.scrollTop > 400);
		};
		document.addEventListener('scroll', onScroll, true);
		return () => document.removeEventListener('scroll', onScroll, true);
	}, []);
	// Entering / leaving full screen swaps the scroll container — start fresh.
	useEffect(() => { setShowScrollTop(false); }, [isFullScreen]);
	const scrollToTop = useCallback(() => {
		scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	const parentTabList = useMemo(() => {
		return parentTabs;
	}, []);

	const {
		rawData,
		statsSummary,
		isGridLoading,
		isStatsLoading,
		fetchData,
		fetchCogsAnalysisGraph,
		cacheTime,
		statsCacheTime,
		refresh,
		refreshStats,
		note,
	} = useAiFmsData({
		parentTab: parentTabList[0],
	});

	const {
		parentTab,
		active,
		selectedTab,
		tableTab,
		mealTabsList,
		data,
		setSelectedTab,
		handleParentTab,
		handleMealTab,
		handleTableTab,
		getTabInfo,
		getAvailableTabs,
		parentTabVersion,
		setSelectSubTab,
		selectSubTab,
		setPerUnit,
		perUnit,
		selectedDashboard,
		setSelectedDashboard,
		operationalDataView,
		setOperationalDataView,
	} = useTableState([], setSearch, isNagaCustomer, isDanaChogaCustomer, parentTabList, filters?.fromPodFilter?.id ?? null);

	const {
		getTableInfo,
		dropDownItems,
	} = useTableData(rawData, parentTab, search, isGridLoading, selectedTab);

	useEffect(() => {
		if (parentValue) {
			const cleanParentValue = parentValue.replace(/^"(.*)"$/, '$1');
			const foundParentTab = parentTabList.find(tab => tab.value === cleanParentValue);
			if (foundParentTab) {
				handleParentTab(foundParentTab);
			}
		}
		removeKeyFromUrl('parentValue');
	}, [handleParentTab]);


	const filtersInfo = {
		header: {
			store: false, date: false, product: false, process: false,
		},
		kitchenBaseStore: {
			text: 'Kitchen ', show: true, mandatory: true,
		},
		productWithVariants: {
			text: 'Product',
			show: true,
		},
		pods: {
			text: 'Pods', show: false,
		},
		allProcessType: {
			text: 'All Process Type', show: true, multiSelect: true,
		},
		dateRangeWithType: {
			text: 'Date Range',
			show: true,
		},
		timeType: {
			text: 'Date Time Type',
			show: true,
		},
		orderType: {
			text: 'Tab Type',
		},
	};

	const handleParentTabChange = useCallback(async (tab) => {
		handleParentTab(tab);
		setSearch({});
		setSelectedTab('weight');
	}, [handleParentTab, filters, fetchData, active, selectedTab, tableTab]);

	// operationalView is passed explicitly by each caller (like overrideRange
	// / batches) — keeps this helper pure and avoids re-creating it on every
	// view toggle.
	const getEffectiveFilters = useCallback((currentFilters, overrideRange, batches, operationalView) => {
		let newFilters = { ...currentFilters };
		if (operationalView != null) {
			newFilters.operationalDataView = operationalView;
		}
		if (overrideRange?.from && overrideRange?.to && currentFilters?.dateRange) {
			newFilters = {
				...newFilters,
				dateRange: {
					...currentFilters.dateRange,
					from: overrideRange.from,
					to: overrideRange.to,
				}
			};
		}
		if (batches && batches.length > 0) {
			const processedBatches = batches.map(b => ({
				...b,
				grnItemBatchId: (b.grnItemBatchId && b.grnItemBatchId.toString().startsWith('D-')) ? null : b.grnItemBatchId
			}));
			const validBatchIds = processedBatches.map(b => b.grnItemBatchId).filter(id => id !== null);

			newFilters = {
				...newFilters,
				...(validBatchIds.length > 0 && { batchIds: validBatchIds }),
				selectedBatches: processedBatches,
			};
		}
		return newFilters;
	}, []);

	const handleSelectDashboard = useCallback((tabId) => {
		setSelectedDashboard(tabId);
		const isBatchMode = tabId === 'batchPerformance';
		if (isBatchMode) {
			setIsBatchPerformanceActive(true);
		} else {
			setSelectedBatches([]);
			setIsBatchPerformanceActive(false);
			setOverrideDateRange({ from: null, to: null });
			if (filters.dateRange) {
				const effectiveFilters = getEffectiveFilters(filters, { from: null, to: null }, [], operationalDataView);
				let effectiveSelectedTab = selectedTab;
				// Cost is available in Planned vs Actual for Issued & Distribution;
				// only fall back to weight for parents where cost isn't allowed there.
				if (tabId === 'plannedVsActual' && selectedTab === 'cost' && !['ISSUED', 'DISPATCHED'].includes(parentTab?.value)) {
					effectiveSelectedTab = 'weight';
					setSelectedTab('weight');
				}
				const isPerUnit = effectiveSelectedTab === 'cost' && selectSubTab === 'perUnit';
				fetchData({
					...effectiveFilters,
					tableTab,
					parentTab,
					shiftId: active?.id,
					selectedTab: effectiveSelectedTab,
					isPerUnit,
					isBatchWiseMode: false,
					adjustLeftOver: adjustLeftOverRef.current,
				orderSource: orderSourceRef.current,
				});

				const getSelectedTab = effectiveSelectedTab === 'weight' ? 'TotalWeight' : effectiveSelectedTab === 'cost' ? 'TotalCost' : effectiveSelectedTab === 'count' ? 'TotalCount' : null;
				fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase(), false, { batchIds: effectiveFilters.batchIds, selectedBatches: effectiveFilters.selectedBatches, operationalDataView, fromPodFilter: effectiveFilters?.fromPodFilter }, false, filters?.timeType);
			}
		}
	}, [filters, selectedTab, selectedBatches, selectSubTab, tableTab, parentTab, active, parentTabList, handleParentTab, operationalDataView, getEffectiveFilters, fetchData, fetchCogsAnalysisGraph]);

	const handleMealTabChange = useCallback((tab) => {
		handleMealTab(tab);
		setSearch({});
		const isBatchMode = selectedDashboard === 'batchPerformance';
		if (filters.dateRange) {
			const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
			fetchData({
				...effectiveFilters,
				tableTab,
				parentTab,
				selectedTab,
				shiftId: tab?.id,
				isBatchWiseMode: isBatchMode,
				adjustLeftOver: adjustLeftOverRef.current,
				orderSource: orderSourceRef.current,
			});
			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, tab?.id, getSelectedTab, parentTab?.value.toLowerCase(), false, { batchIds: effectiveFilters.batchIds, selectedBatches: effectiveFilters.selectedBatches, operationalDataView, fromPodFilter: effectiveFilters?.fromPodFilter }, isBatchMode, filters?.timeType);
		}
	}, [handleMealTab, filters, tableTab, parentTab, selectedTab, overrideDateRange, selectedBatches, getEffectiveFilters, operationalDataView, selectedDashboard, fetchData, fetchCogsAnalysisGraph]);

	const handleSetSelectedTab = useCallback((tabId) => {
		setSelectedTab(tabId);
		setSearch({});
		const isBatchMode = selectedDashboard === 'batchPerformance';
		const availableTabs = getAvailableTabs();
		const selectedMainTab = availableTabs.find(tab => tab.id === tabId);
		let defaultSubTabId = null;

		if (selectedMainTab && selectedMainTab.subTabs) {
			const defaultSubTab = selectedMainTab.subTabs.find(sub => sub.default);
			if (defaultSubTab) {
				defaultSubTabId = defaultSubTab.id;
				setSelectSubTab(defaultSubTabId);
			}
		} else {
			setSelectSubTab(null);
		}
		if (filters.dateRange) {
			const isPerUnit = tabId === 'cost' && defaultSubTabId === 'perUnit';
			setPerUnit(isPerUnit);
			const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
			fetchData({
				...effectiveFilters,
				tableTab,
				parentTab,
				selectedTab: tabId,
				shiftId: active?.id,
				isPerUnit,
				isBatchWiseMode: isBatchMode,
				adjustLeftOver: adjustLeftOverRef.current,
				orderSource: orderSourceRef.current,
			});
			const getSelectedTab = tabId === 'weight' ? 'TotalWeight' : tabId === 'cost' ? 'TotalCost' : tabId === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase(), false, { batchIds: effectiveFilters.batchIds, selectedBatches: effectiveFilters.selectedBatches, operationalDataView, fromPodFilter: effectiveFilters?.fromPodFilter }, isBatchMode, filters?.timeType);
		}
	}, [setSelectedTab, filters, parentTab, tableTab, active, getAvailableTabs, overrideDateRange, selectedBatches, getEffectiveFilters, operationalDataView, selectedDashboard, fetchData, fetchCogsAnalysisGraph]);

	const handleSetSelectedSubTab = useCallback((subTabId) => {
		setSelectSubTab(subTabId);
		setSearch({});
		const isBatchMode = selectedDashboard === 'batchPerformance';
		if (filters.dateRange) {
			const isPerUnit = selectedTab === 'cost' && subTabId === 'perUnit';
			setPerUnit(isPerUnit);
			const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
			fetchData({
				...effectiveFilters,
				tableTab,
				parentTab,
				selectedTab,
				shiftId: active?.id,
				isPerUnit,
				isBatchWiseMode: isBatchMode,
				adjustLeftOver: adjustLeftOverRef.current,
				orderSource: orderSourceRef.current,
			});
			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase(), false, { batchIds: effectiveFilters.batchIds, selectedBatches: effectiveFilters.selectedBatches, operationalDataView, fromPodFilter: effectiveFilters?.fromPodFilter }, isBatchMode, filters?.timeType);
		}
	}, [setSelectedTab, filters, parentTab, tableTab, active, selectedTab, selectSubTab, overrideDateRange, selectedBatches, getEffectiveFilters, operationalDataView, selectedDashboard, fetchData, fetchCogsAnalysisGraph]);


	const handleDataFetch = useCallback((filter) => {
		const processedFilters = { ...filter };
		if (processedFilters?.store) {
			processedFilters.kitchenBaseStore = processedFilters.store;
		}
		// Shifts belong to a kitchen, so switching kitchens invalidates whichever
		// shift the strip is holding - drop back to 'All' instead of querying a
		// shift id the new kitchen may not have. Batched with the setFilters below,
		// so the fetch effect already sees 'All'.
		if (
			filters?.fromPodFilter?.id &&
			processedFilters?.fromPodFilter?.id !== filters?.fromPodFilter?.id
		) {
			handleMealTab({ id: -999, shiftName: 'All' });
		}
		if (processedFilters?.dateRange) {
			processedFilters.dateRange.active = true;
		}

		const isSameFromMain = moment(filters?.dateRange?.from).isSame(moment(filter?.dateRange?.from), 'day');
		const isSameToMain = moment(filters?.dateRange?.to).isSame(moment(filter?.dateRange?.to), 'day');

		if (selectedDashboard === 'batchPerformance') {
			if (isSameFromMain && isSameToMain) {
				setFilters(processedFilters);
				return;
			}
			setSelectedBatches([]);
			setIsBatchPerformanceActive(true);
			setOverrideDateRange(processedFilters?.dateRange);
			skipFetchRef.current = true; // Block background API call on date change
			setFilters(processedFilters);
			return;
		}

		// Reset override when main filters change to something different
		const isSameFrom = moment(overrideDateRange?.from).isSame(moment(filter?.dateRange?.from), 'day');
		const isSameTo = moment(overrideDateRange?.to).isSame(moment(filter?.dateRange?.to), 'day');
		if (overrideDateRange?.from && (!isSameFrom || !isSameTo)) {
			setOverrideDateRange({ from: null, to: null });
			setSelectedBatches([]);
		}
		setFilters(processedFilters);
	}, [filters, overrideDateRange, selectedDashboard, handleMealTab]);


	// Date Time Type is restricted to Scheduled At here, so seed it as the initial
	// value as well - ImagesFinder otherwise starts from its shared 'annotation'
	// default and submits that on the first fetch whenever the page is opened with
	// query params (deep links from Process Flow, or a reload of the serialized
	// filters), since only its no-query branch reads timeTypeArray.
	const imagesFinderInitialState = useMemo(() => {
		return {
			timeType: dateTypeArr[0],
			...(filters?.dateRange?.from ? { dateRange: filters.dateRange } : {}),
		};
	}, [filters?.dateRange?.from, filters?.dateRange?.to]);

	const imagesFinderDefaultState = useMemo(() => {
		return {
			...(filters?.dateRange ? { DATE_RANGE: filters.dateRange } : {}),
			timeTypeArray: dateTypeArr
		};
	}, [filters?.dateRange]);


	const handleBatchDataFetch = useCallback((updatedBatches, customDateRange = null) => {
		skipFetchRef.current = false;
		setSelectedBatches(updatedBatches);

		// If a custom date was provided, update overrideDateRange + filters.dateRange
		// (so ImagesFinder reflects the new date)
		if (customDateRange?.from && customDateRange?.to) {
			setOverrideDateRange(customDateRange);
			setFilters((prev) => ({
				...prev,
				dateRange: {
					...(prev?.dateRange || {}),
					from: customDateRange.from,
					to: customDateRange.to,
					active: true,
				},
			}));
		} else {
			// Trigger a refresh for the current filters with new batches
			setFilters(prev => ({ ...prev }));
		}
	}, [filters, selectedTab, selectedBatches, selectSubTab, tableTab, parentTab, active, parentTabList]);

	const handleOrderSourceChange = useCallback((newVal) => {
		if (newVal === orderSourceRef.current) return;
		setOrderSource(newVal);
		orderSourceRef.current = newVal;
		if (filters?.dateRange) {
			const isBatchMode = selectedDashboard === 'batchPerformance';
			const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
			const isPerUnit = selectedTab === 'cost' && selectSubTab === 'perUnit';
			fetchData({
				...effectiveFilters,
				tableTab,
				parentTab,
				shiftId: active?.id,
				selectedTab,
				isPerUnit,
				isBatchWiseMode: isBatchMode,
				adjustLeftOver: adjustLeftOverRef.current,
				orderSource: newVal,
			});
		}
	}, [filters, overrideDateRange, selectedBatches, selectedTab, selectSubTab, tableTab, parentTab, active, selectedDashboard, getEffectiveFilters, operationalDataView, fetchData]);

	// Only Issued and Primary Packaging resolve the plan link on the backend; the other
	// parent tabs read from the nightly costing summaries, which carry no order reference.
	const showOrderSourceTabs = selectedDashboard === 'plannedVsActual'
		&& ORDER_SOURCE_PARENT_TABS.includes(parentTab?.value);

	// Leaving a supported tab would otherwise strand a filter the user can no longer see.
	useEffect(() => {
		if (!showOrderSourceTabs && orderSourceRef.current !== 'combined') {
			setOrderSource('combined');
			orderSourceRef.current = 'combined';
		}
	}, [showOrderSourceTabs]);

	const handleAdjustLeftOverChange = useCallback((newVal) => {
		setAdjustLeftOver(newVal);
		adjustLeftOverRef.current = newVal;
		if (filters?.dateRange) {
			const isBatchMode = selectedDashboard === 'batchPerformance';
			const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
			const isPerUnit = selectedTab === 'cost' && selectSubTab === 'perUnit';
			fetchData({
				...effectiveFilters,
				tableTab,
				parentTab,
				shiftId: active?.id,
				selectedTab,
				isPerUnit,
				isBatchWiseMode: isBatchMode,
				adjustLeftOver: newVal,
				orderSource: orderSourceRef.current,
			});
		}
	}, [filters, overrideDateRange, selectedBatches, selectedTab, selectSubTab, tableTab, parentTab, active, selectedDashboard, getEffectiveFilters, operationalDataView, fetchData]);

	useEffect(() => {
		// Kitchen scopes every number on this screen, so nothing is fetched until the
		// drawer has one - the first store list to land fills it in and re-triggers
		// this effect through `filters`.
		if (filters?.dateRange && filters?.fromPodFilter?.id && tableTab && parentTab && parentTabVersion > 0) {
			if (skipFetchRef.current) {
				skipFetchRef.current = false;
				return;
			}
			const isBatchMode = selectedDashboard === 'batchPerformance';
			const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
			const isPerUnit = selectedTab === 'cost' && selectSubTab === 'perUnit';
			setPerUnit(isPerUnit);
			fetchData({
				...effectiveFilters,
				tableTab,
				parentTab,
				shiftId: active?.id,
				selectedTab,
				isPerUnit,
				isBatchWiseMode: isBatchMode,
				adjustLeftOver: adjustLeftOverRef.current,
				orderSource: orderSourceRef.current,
			});
		}
	}, [parentTabVersion, filters]);

	// const handleSingleDateChange = useCallback((date) => {
	// 	setOverrideDateRange({ from: date, to: date });
	// 	setSelectedBatches([]);
	// 	const isBatchMode = selectedDashboard === 'batchPerformance';
	// 	if (filters?.dateRange) {
	// 		const effectiveFilters = getEffectiveFilters(filters, { from: date, to: date }, []);
	// 		fetchData({
	// 			...effectiveFilters,
	// 			tableTab,
	// 			parentTab,
	// 			shiftId: active?.id,
	// 			selectedTab,
	// 			isBatchWiseMode: isBatchMode
	// 		});

	// 		const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
	// 		fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase(), false, {}, isBatchMode);
	// 	}
	// }, [filters, tableTab, parentTab, active, selectedTab, fetchData, fetchCogsAnalysisGraph, getEffectiveFilters, selectedDashboard]);

	const exportData = () => {
		const headers = [];
		let csvData = [];
		if (isDanaChogaCustomer && !['wastage'].includes(parentTab?.value.toLowerCase()) && selectedTab !== 'qrCount') {
			const isDispatched = ['dispatched'].includes(parentTab?.value.toLowerCase());
			const isPrimaryPackaging = ['primary_packaging'].includes(parentTab?.value.toLowerCase());
			rawData?.columns?.forEach((column, index) => {
				if (index === 0 && column?.label?.toLowerCase() !== 'total') {
					headers.push(column?.label);
					if (selectedTab === 'weight') {
						headers.push('Output Unit');
					}
					headers.push('Unit');
				} else if (column?.label?.toLowerCase() === 'ega') {
					headers.push(column?.label);
				} else if (column?.label?.toLowerCase() !== 'total') {
					if (selectedTab === 'cost' || selectedTab === 'count') {
						headers.push(column?.label, column?.label, column?.label);
					} else {
						headers.push(column?.label, column?.label, column?.label, column?.label, column?.label, column?.label);
					}
				}
			});

			if (selectedTab === 'cost' || selectedTab === 'count') {
				headers.push('Total', 'Total', 'Total');
			} else {
				headers.push('Total', 'Total', 'Total', 'Total', 'Total', 'Total');
			}

			const subHeaders = [];
			rawData?.columns?.forEach((column, index) => {
				if (index === 0 && column?.label?.toLowerCase() !== 'total') {
					subHeaders.push('');
					if (selectedTab === 'weight') {
						subHeaders.push('');
					}
					subHeaders.push('');
				} else if (column?.label?.toLowerCase() === 'ega') {
					subHeaders.push('');
				} else if (column?.label?.toLowerCase() !== 'total') {
					if (selectedTab === 'cost') {
						subHeaders.push('Ordered Cost', 'Fulfilled Cost', 'Shortfall Cost');
					} else if (selectedTab === 'count') {
						subHeaders.push('Ordered Qty', 'Fulfilled Qty', 'Shortfall Qty');
					} else if (selectedTab === 'weight' && !isDispatched) {
						subHeaders.push('Ordered Weight', 'Fulfilled Weight', 'Ordered Qty', 'Fulfilled Qty', 'Fulfilled Fall Short Weight', 'Fulfilled Fall Short Qty');
					} else if (isDispatched) {
						subHeaders.push('Ordered Weight', 'dispatched Weight', 'Ordered Qty', 'dispatched Qty', 'Dispatched Fullfiled Fall Short Weight', 'Dispatched Fullfiled Fall Short Qty');
					} else {
						subHeaders.push('Ordered Weight', 'Fulfilled Weight', 'Ordered Qty', 'Fulfilled Qty', 'Fulfilled Fall Short Weight', 'Fulfilled Fall Short Qty');
					}
				}
			});

			if (selectedTab === 'cost') {
				subHeaders.push('Total Ordered Cost', 'Total Fulfilled Cost', 'Total Shortfall Cost');
			} else if (selectedTab === 'count') {
				subHeaders.push('Total Ordered Qty', 'Total Fulfilled Qty', 'Total Shortfall Qty');
			} else {
				subHeaders.push('Total Ordered Weight', 'Total Fulfilled Weight', 'Total Ordered Qty', 'Total Fulfilled Qty', 'Total Fall Short Weight', 'Total Fall Short Qty');
			}
			const rows = rawData?.data?.map((item) => {
				const row = [];
				// Initialize totals
				let totalOrdered1 = 0;
				let totalFulfilled1 = 0;
				let totalShortfall1 = 0;

				let totalOrderedWeight = 0;
				let totalFulfilledWeight = 0;
				let totalOrderedQty = 0;
				let totalFulfilledQty = 0;
				let totalShortFallWeight = 0;
				let totalShortFallQty = 0;
				rawData?.columns?.forEach((header, index) => {
					const cellData = item?.[header?.key];

					if (index === 0 && header?.label?.toLowerCase() !== 'total') {
						const value = (cellData && typeof cellData === 'object' && cellData.value !== undefined)
							? cellData.value
							: (cellData ?? '');
						const splitValue = value.includes('¦') ? value.split('¦')[0] : value;
						const splitUnit = value.includes('¦') ? value.split('¦')[1] : null;
						row.push(splitValue);
						if (selectedTab === 'weight') {
							row.push("kg");
						}
						row.push(splitUnit ?? "-");
					} else if (header?.label?.toLowerCase() === 'ega') {
						row.push(cellData?.value ?? '-');
					} else if (header?.label?.toLowerCase() !== 'total') {
						if (cellData && typeof cellData === 'object') {
							const unit = extractUnit(cellData?.value)?.toLowerCase();
							const isKg = unit === 'kg';
							const isPcs = unit === 'pcs';

							if (selectedTab === 'cost') {
								const ord = cellData.orderedCost ?? 0;
								const full = cellData.dispatchedCost ?? cellData.fullfilledCost ?? 0;
								const short = calcShortfall(ord, full);

								row.push(formatNum(ord));
								row.push(formatNum(full));
								row.push(short);

								totalOrdered1 += Number(ord);
								totalFulfilled1 += Number(full);

							} else if (selectedTab === 'count') {
								// Parse "Fulfilled / Ordered" string e.g., "129.00 pcs / 130.00 pcs"
								let ord = 0;
								let full = 0;
								const valStr = cellData.value || "";
								if (valStr.includes('/')) {
									const parts = valStr.split('/');
									full = parseFloat(parts[0]?.replace(",", '')) || 0;
									ord = parseFloat(parts[1]?.replace(",", '')) || 0;
								} else {
									ord = cellData.orderedQuantity ?? 0;
									full = cellData.dispatchedQuantity ?? cellData.gross_qty ?? cellData.grossProducedQuantity ?? 0;
								}
								const short = calcShortfall(ord, full, false);

								row.push(formatNum(ord));
								row.push(formatNum(full));
								row.push(short);

								totalOrdered1 += Number(ord);
								totalFulfilled1 += Number(full);

							} else {
								// Weight Logic (Existing)
								let fulfilledWeight = '';
								let orderedWeight = '';
								let fulfilledQty = '';
								let orderedQty = '';
								let fulfilledFallShortWeight = '';
								let fulfilledFallShortQty = '';

								if (isDispatched) {
									orderedWeight = isKg ? formatNum(cellData.orderedQuantity) : '';
									fulfilledWeight = isKg ? formatNum(cellData.dispatchedQuantity) : '';
									orderedQty = orderedWeight;
									fulfilledQty = fulfilledWeight;
									fulfilledFallShortWeight = isKg ? calcShortfall(cellData.orderedQuantity, cellData.dispatchedQuantity) : '';
									// fulfilledFallShortQty = isPcs ? calcShortfall(cellData.orderedQuantity, cellData.dispatchedQuantity) : '';
									fulfilledFallShortQty = fulfilledFallShortWeight;
								} else {
									const valStr = cellData.value || "";
									if (valStr.includes('/')) {
										const parts = valStr.split('/');
										fulfilledWeight = parseFloat(parts[0]?.replace(",", '')) || 0;
										fulfilledQty = fulfilledWeight
										orderedWeight = parseFloat(parts[1]?.replace(",", '')) || 0;
										orderedQty = orderedWeight
										fulfilledFallShortWeight = calcShortfall(orderedQty, fulfilledQty);
										fulfilledFallShortQty = calcShortfall(orderedQty, fulfilledQty);
									}
									// orderedWeight = formatNum(cellData.orderedWeight);
									// fulfilledWeight = formatNum(cellData.producedWeight);
									// orderedQty = formatNum(cellData.orderedQuantity);
									// fulfilledQty = formatNum(cellData.gross_qty) || formatNum(cellData.grossProducedQuantity);
									// fulfilledFallShortWeight = calcShortfall(cellData.orderedWeight, cellData.producedWeight);
									// fulfilledFallShortQty = calcShortfall(cellData.orderedQuantity, cellData.gross_qty);
								}

								const addNum = (val) => (val !== '' && !isNaN(Number(val))) ? Number(val) : 0;
								totalOrderedWeight += addNum(orderedWeight);
								totalFulfilledWeight += addNum(fulfilledWeight);
								totalOrderedQty += addNum(orderedQty);
								totalFulfilledQty += addNum(fulfilledQty);
								totalShortFallWeight += addNum(fulfilledFallShortWeight);
								totalShortFallQty += addNum(fulfilledFallShortQty);
								row.push(orderedWeight, fulfilledWeight, orderedQty, fulfilledQty, fulfilledFallShortWeight, fulfilledFallShortQty);
							}
						} else {
							if (selectedTab === 'cost' || selectedTab === 'count') {
								row.push('', '', '');
							} else {
								row.push('', '', '', '', '', '');
							}
						}
					}
				});

				// Totals Row Push
				if (selectedTab === 'cost' || selectedTab === 'count') {
					totalShortfall1 = totalOrdered1 - totalFulfilled1;
					row.push(
						formatNum(totalOrdered1),
						formatNum(totalFulfilled1),
						formatNum(totalShortfall1)
					);
				} else {
					row.push(
						formatNum(totalOrderedWeight),
						formatNum(totalFulfilledWeight),
						formatNum(totalOrderedQty),
						formatNum(totalFulfilledQty),
						formatNum(totalShortFallWeight),
						formatNum(totalShortFallQty)
					);
				}

				return row;
			});

			csvData = [headers, subHeaders, ...rows];
		}
		else {
			rawData?.columns?.forEach((column, index) => {
				headers.push(column?.label);
				if (index === 0 && rawData?.data[1]?.[rawData?.columns[0]?.key]?.value?.includes('¦')) {
					headers.push('Unit');
				}
			});
			const rows = rawData?.data?.map((item) => {
				const row = [];
				rawData?.columns?.forEach((header, index) => {
					const cellData = item?.[header?.key];
					const value = (cellData && typeof cellData === 'object' && cellData.value !== undefined)
						? cellData.value
						: (cellData ?? '');
					const splitValue = index === 0 && value.includes('¦') ? value.split('¦')[0] : value;
					const splitUnit = index === 0 && value.includes('¦') ? value.split('¦')[1] : null;
					row.push(splitValue);
					if (index == 0 && rawData?.data[1]?.[rawData?.columns[0]?.key]?.value?.includes('¦')) {
						row.push(splitUnit ?? "-");
					}
				});
				return row;
			});
			csvData = [headers, ...rows];
		}

		// Quote every cell that has a comma / quote / line break (e.g. Placed At "15-Oct, 08:45 AM"),
		// otherwise Excel splits it into extra cells and every later column shifts right.
		const toCsvCell = (cell) => {
			const str = String(cell ?? '');
			return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
		};
		const csvContent = csvData.map((row) => row.map(toCsvCell).join(',')).join('\n');
		// BOM so Excel reads UTF-8 (keeps "–", "₹" etc. readable instead of garbled characters).
		const csvFile = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8' });
		const downloadLink = document.createElement('a');
		downloadLink.download = `AI_FMS_SUMMARY-${moment().format('YYYY-MM-DD')}.csv`;
		downloadLink.href = window.URL.createObjectURL(csvFile);
		downloadLink.style.display = 'none';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		window.URL.revokeObjectURL(downloadLink.href);
		document.body.removeChild(downloadLink);
	}

	useEffect(() => {
		const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, selectedBatches, operationalDataView);
		const isBatchMode = selectedDashboard === 'batchPerformance';
		if (effectiveFilters?.dateRange) {
			const getSelectedTab = selectedTab === 'weight' ? 'TotalWeight' : selectedTab === 'cost' ? 'TotalCost' : selectedTab === 'count' ? 'TotalCount' : null;
			fetchCogsAnalysisGraph(effectiveFilters.dateRange, active?.id, getSelectedTab, parentTab?.value.toLowerCase(), false, { batchIds: effectiveFilters.batchIds, selectedBatches: effectiveFilters.selectedBatches, operationalDataView, fromPodFilter: effectiveFilters?.fromPodFilter }, isBatchMode, filters?.timeType);
		}
	}, [filters, parentTab, operationalDataView]);

	const FilterSection = useMemo(() => {
		const effectiveFilters = getEffectiveFilters(filters, overrideDateRange, [], operationalDataView);
		return (
			<div className='flex flex-1 items-baseline gap-3'>
				<div className='flex-1 min-w-0'>
					<GlobalSearch
						placeholderText='Search'
						dropDownOptions={dropDownItems}
						searchDropDown={dropDownItems?.length > 0}
						setSelectedValue={setSearch}
					/>
				</div>
				<div className='flex-shrink-0'>
					{filtersInfoChip(filtersInfo, effectiveFilters)}
				</div>
				<Button
					onClick={() => dispatch(drawerActions.toggleFiltersDrawer(true, {
						handleDataExport: (filtersState) => {
							exportData();
						},
					}))}
					className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700 flex-shrink-0'
				>
					<span>Filter & Export</span>
					<Filter size={16} />
				</Button>
			</div>
		);
	}, [filters, overrideDateRange, filtersInfo, getEffectiveFilters, dispatch, parentTab, rawData, operationalDataView, selectedTab, selectSubTab, active]);

	const ParentTabsSection = useMemo(() => {
		const disabledValues = ['DISPATCHED'];
		const isBatchMode = selectedDashboard === 'batchPerformance';

		return (
			<div className='flex w-fit justify-center rounded-md bg-[#FDE6AD] border border-[#00000033]'>
				{parentTabList?.map((ele) => {
					const isActive = parentTab?.id === ele?.id;
					const isDisabled = isBatchMode && disabledValues.includes(ele.value);

					return (
						<div
							key={ele.id}
							className={`flex items-center gap-2 justify-center px-3 py-2 text-[11.5px] font-medium whitespace-nowrap
              ${isActive ? 'bg-[#ffb703]' : ''} 
              ${isDisabled ? 'opacity-40 cursor-not-allowed pointer-events-none grayscale' : 'cursor-pointer'}`}
							onClick={() => !isDisabled && handleParentTabChange(ele)}
						>
							<img src={ele?.icon} alt="icon" className="w-6 h-6" />
							{ele?.header}
						</div>
					);
				})}
			</div>
		);
	}, [parentTab, handleParentTabChange, selectedDashboard]);

	const OperationalDataTabsSection = useMemo(() => {
		const activeTab = operationalDataTabs.find((ele) => ele.id === operationalDataView);
		return (
			<div className='flex items-center gap-3'>
				{/* Same visual language as ParentTabsSection — cream container,
				    active tab gets a solid yellow pill within. */}
				<div className='flex w-fit justify-center rounded-md bg-[#FDE6AD] border border-[#00000033] overflow-hidden'>
					{operationalDataTabs.map((ele) => {
						const isActive = operationalDataView === ele.id;
						const isComingSoon = ele?.comingSoon === true;
						return (
							<div
								key={ele.id}
								onClick={() => !isComingSoon && setOperationalDataView(ele.id)}
								className={`flex items-center gap-2 justify-center px-4 py-2 text-[12px] font-medium whitespace-nowrap transition-colors
									${isActive ? 'bg-[#ffb703] text-gray-900 font-semibold' : 'text-gray-700 hover:bg-[#fde2a0]'}
									${isComingSoon ? 'opacity-40 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
							>
								<span>{ele.header}</span>
								{ele?.isNew && (
									<span className='inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-bold bg-gray-900 text-white tracking-wide'>
										NEW
									</span>
								)}
								{isComingSoon && (
									<span className='inline-flex items-center px-1.5 py-[1px] rounded text-[9px] font-bold bg-gray-300 text-gray-600'>
										SOON
									</span>
								)}
							</div>
						);
					})}
				</div>
				{/* Dynamic subtitle — describes what the active view shows */}
				{activeTab?.subtitle && (
					<span className='text-[12.5px] text-gray-500 hidden sm:inline'>
						Viewing <span className='font-semibold text-gray-700'>{activeTab.subtitle}</span> metrics across the supply chain
					</span>
				)}
			</div>
		);
	}, [operationalDataView, setOperationalDataView]);

	const DashboardTabsSelection = useMemo(() => {
		const isDistribution = parentTab?.value === 'DISPATCHED';
		return (
			<div className='flex w-fit justify-center rounded-md bg-[#FDE6AD] border border-[#00000033]'>
				{dashboardTabs.map((ele) => {
					const isActive = selectedDashboard === ele.id;
					const isDisabled = isDistribution && ele.id === 'batchPerformance';
					return (
						<div
							key={ele.id}
							className={`flex items-center justify-center px-3 h-[40px] text-[11.5px] font-medium whitespace-nowrap
              ${isActive ? 'bg-[#ffb703]' : ''}
              ${isDisabled ? 'opacity-40 cursor-not-allowed pointer-events-none grayscale' : 'cursor-pointer'}`}
							onClick={() => !isDisabled && handleSelectDashboard(ele.id)}
						>
							{ele.header}
						</div>
					);
				})}
			</div>
		);
	}, [selectedDashboard, handleSelectDashboard, parentTab]);

	const isMealTabsDisabled = (selectedBatches?.length ?? 0) > 0;

	// Process Data → Distribution has no shift strip. Drop back to 'All' so a shift picked
	// on another tab doesn't keep filtering the data with no way to see or change it.
	const isProcessDistribution = operationalDataView === 'processAnalysis' && parentTab?.value === 'DISPATCHED';
	useEffect(() => {
		if (!isProcessDistribution || active?.id == null || active?.id === -999) return;
		const allTab = mealTabsList?.find((ele) => ele?.id === -999) ?? { id: -999, shiftName: 'All' };
		handleMealTabChange(allTab);
	}, [isProcessDistribution]);
	const MealTabsSection = useMemo(() => (
		<div
			className={`inline-flex overflow-x-auto max-w-[27rem] rounded-md bg-[#FDE6AD] border border-[#00000033] ${isMealTabsDisabled ? 'opacity-50 pointer-events-none' : ''}`}
			title={isMealTabsDisabled ? 'Clear selected batches to switch shift' : undefined}
		>
			{mealTabsList.map((ele) => {
				const isActive = active?.id === ele?.id;
				return (
					<button
						key={ele.id}
						disabled={isMealTabsDisabled}
						className={`px-3.5 py-2 text-sm font-medium rounded-md whitespace-nowrap ${isActive
							? 'bg-yellow-400 text-black'
							: 'text-gray-700 hover:bg-yellow-300/50'
							} ${isMealTabsDisabled ? 'cursor-not-allowed' : ''}`}
						onClick={() => handleMealTabChange(ele)}
					>
						{ele?.shiftName}
					</button>
				);
			})}
		</div>
	), [mealTabsList, active, handleMealTabChange, isMealTabsDisabled]);

	const subMealTabsSection = useMemo(() => {
		const availableTabs = getAvailableTabs();
		const selectedMainTab = availableTabs.find(tab => tab.id === selectedTab);
		// console.log(parentTab?.value,parentTab?.value !== 'PRODUCED' && parentTab?.value !== 'SECONDARY_PACKAGING' && parentTab?.value !== 'PRIMARY_PACKAGING', " ccc ");
		if (parentTab?.value !== 'PRODUCED' && parentTab?.value !== 'SECONDARY_PACKAGING' && parentTab?.value !== 'PRIMARY_PACKAGING') return;
		return (
			<div className='inline-flex rounded-md bg-[#FDE6AD] border border-[#00000033]'>
				{selectedMainTab && selectedMainTab.subTabs && Array.isArray(selectedMainTab.subTabs) && (
					selectedMainTab.subTabs.map((subTab) => (
						<button
							key={subTab.id}
							onClick={() => handleSetSelectedSubTab(subTab.id)}
							className={`px-3.5 py-2 flex items-center text-sm font-medium rounded-md whitespace-nowrap ${selectSubTab === subTab.id
								? 'bg-yellow-400 text-black'
								: 'text-gray-700 hover:bg-yellow-300/50'
								}`}
						>
							<span className='mr-1'>{subTab.icon}</span>
							{subTab.lable}
						</button>
					))
				)}
			</div>
		);
	}, [getAvailableTabs, selectedTab, setSelectedTab, selectSubTab, parentTab?.value, handleSetSelectedSubTab]);

	const TabsSection = useMemo(() => (
		<div className='inline-flex rounded-md bg-[#FDE6AD] border border-[#00000033]'>
			{getAvailableTabs().map((tab) => (
				<button
					key={tab.id}
					onClick={() => handleSetSelectedTab(tab.id)}
					disabled={tab.disabled}
					title={tab.tooltip || ''}
					className={`px-3.5 py-2 flex items-center text-sm font-medium rounded-md whitespace-nowrap ${tab.disabled
						? 'text-gray-400 cursor-not-allowed opacity-60 bg-transparent'
						: selectedTab === tab.id
							? 'bg-yellow-400 text-black'
							: 'text-gray-700 hover:bg-yellow-300/50'
						}`}
				>
					<span className='mr-1'>{tab.icon}</span>
					{tab.label}
				</button>
			))}
		</div>
	), [getAvailableTabs, selectedTab, setSelectedTab, parentTab?.value, handleSetSelectedTab]);

	const renderCustomLegend = useCallback(() => {
		const isCostFormat = selectedTab === 'cost';
		const pVal = parentTab?.value;

		const Dot = ({ color, label, border, tip }) => (
			<div
				className='flex items-center gap-1 shrink-0 cursor-help'
				title={tip || label}
			>
				<span
					className='inline-block w-2.5 h-2.5 rounded-full'
					style={{ backgroundColor: color, border: border || 'none' }}
				/>
				<span className='text-[11px] text-slate-700 leading-none'>{label}</span>
			</div>
		);

		// Per-parent-tab legend items — only show what's relevant
		// `tip` prop adds a native hover tooltip explaining the threshold / reason
		const legendItems = (() => {
			// Backend-driven legend wins: [{ label, color, tip?, border? }]
			if (Array.isArray(rawData?.legend) && rawData.legend.length > 0) {
				const COLOR_MAP = { green: '#16a34a', orange: '#f97316', red: '#dc2626', black: '#111827', grey: '#6b7280', gray: '#6b7280', blue: '#2563eb', purple: '#9333ea' };
				return (
					<>
						{rawData.legend.map((item) => (
							<Dot key={item.label} color={COLOR_MAP[item.color] || item.color} label={item.label} tip={item.tip} border={item.border} />
						))}
					</>
				);
			}
			switch (pVal) {
				case 'OVERVIEW':
					return (
						<>
							<Dot color='#16a34a' label='Issued' tip='Ingredients-issued column — colored when value > 0' />
							<Dot color='#2563eb' label='Produced' tip='Produced / EGA column — colored when value > 0' />
							<Dot color='#9333ea' label='Packaging' tip='Primary & Secondary Packaging columns — colored when value > 0' />
							<Dot color='#f97316' label='Dispatch' tip='Dispatch column — colored when value > 0' />
							<Dot color='#dc2626' label='Wastage / Loss' tip='Wastage / Production Loss column — colored when value > 0' />
							<Dot color='#e5e7eb' label='Zero' border='1px solid #cbd5e1' tip='Value is 0 — shown in grey' />
						</>
					);
				case 'ISSUED':
					return (
						<>
							<Dot
								color='#16a34a'
								label='OK'
								tip={isCostFormat ? 'Within budget — cost ratio ≤ 105%' : 'Fully issued — issued ratio ≥ 95% of required'}
							/>
							<Dot
								color='#f97316'
								label='Warning'
								tip={isCostFormat ? 'Slightly over budget — 105% < cost ratio ≤ 120%' : 'Under-issued — 80% ≤ issued ratio < 95%'}
							/>
							<Dot
								color='#dc2626'
								label={isCostFormat ? '> 120% over budget' : '< 80% issued'}
								tip={isCostFormat ? 'Significantly over budget — cost ratio > 120%' : 'Critically under-issued — issued ratio < 80% of required'}
							/>
							<Dot
								color='#0018F9'
								label='LO = leftover'
								tip='LO chip — leftover quantity exists for this ingredient'
							/>
						</>
					);
				case 'PRODUCED':
					return (
						<>
							<Dot color='#2563eb' label='Produced' tip='Produced / EGA — colored when value > 0' />
							<Dot color='#dc2626' label='Wastage / Loss' tip='Wastage / Production Loss — colored when value > 0' />
							<Dot color='#e5e7eb' label='Zero' border='1px solid #cbd5e1' tip='Value is 0 — shown in grey' />
						</>
					);
				case 'PRIMARY_PACKAGING':
				case 'SECONDARY_PACKAGING':
					return (
						<>
							<Dot color='#9333ea' label='Packaging' tip='Packaging quantity — colored when value > 0' />
							<Dot color='#dc2626' label='Wastage / Loss' tip='Wastage / Production Loss — colored when value > 0' />
							<Dot color='#e5e7eb' label='Zero' border='1px solid #cbd5e1' tip='Value is 0 — shown in grey' />
						</>
					);
				case 'DISPATCHED':
					if (selectedTab === 'orderPlacement') {
						// Order Placement: Status colours (same colours as the backend).
						const groupLabel = (text) => <span className='text-[11px] font-semibold text-gray-500'>{text}</span>;
						return (
							<>
								{groupLabel('Status:')}
								<Dot color='#2f855a' label='On Time' tip='Order placed before the outlet’s usual order window closed' />
								<Dot color='#dc2626' label='Delayed' tip='Order placed after the outlet’s usual order window closed' />
								<Dot color='#b7791f' label='Awaiting order' tip='No order yet, but the order window has not closed yet' />
								<Dot color='#111827' label='Not placed' tip='No order and the order window has already closed' />
								<Dot color='#6b7280' label='No history' tip='No past orders in the last 20 days to set an order window' />
							</>
						);
					}
					if (selectedTab === 'fifoAdherence') {
						// FIFO Adherence (dispatch only): % of items sent oldest-expiry first.
						return (
							<>
								<Dot color='#16a34a' label='> 90% FIFO' tip='Over 90% of items were dispatched oldest-expiry first' />
								<Dot color='#f97316' label='75–90%' tip='Some items were sent while an older item of the same product was still in stock' />
								<Dot color='#dc2626' label='< 75%' tip='Many items were sent while older items of the same product were still in stock' />
								<Dot color='#111827' label='No scans' tip='Nothing was scanned at dispatch, so FIFO cannot be checked' />
							</>
						);
					}
					if (isProcessDistribution && (selectedTab === 'drTime' || selectedTab === 'avgPerHour')) {
						// Flat order list. Time colour = scan SPEED (seconds per QR), not the length of the time.
						// No Status legend — the status column spells it out. Avg / Hour cells aren't coloured.
						if (selectedTab !== 'drTime') return null;
						// Overview (every step per order) has no speed colouring - nothing to explain.
						if (String(tableTab || '').toLowerCase() === 'overview') return null;
						const isReceiveView = String(tableTab || '').toLowerCase() === 'receive';
						const groupLabel = (text) => <span className='text-[11px] font-semibold text-gray-500'>{text}</span>;
						return (
							<>
								{groupLabel(isReceiveView ? 'Receive time (verify speed):' : 'Dispatch time (scan speed):')}
								<Dot color='#16a34a' label='≤ 10 s / QR' tip='Fast: 10 seconds or less per QR scanned (e.g. 12 QRs in 1 minute)' />
								<Dot color='#f97316' label='10–30 s / QR' tip='Slow: 10 to 30 seconds per QR scanned' />
								<Dot color='#dc2626' label='> 30 s / QR' tip='Very slow: more than 30 seconds per QR scanned' />
							</>
						);
					}
					if (selectedTab === 'drTime' || selectedTab === 'avgPerHour') {
						// D&R Time / Avg per hour: colours are the scan flag (Dispatch View) or receive stage (Receive View).
						return String(tableTab || '').toLowerCase() === 'receive' ? (
							<>
								<Dot color='#16a34a' label='Verified' tip='Unloading and verification completed — time = unload + verify duration' />
								<Dot color='#f97316' label='In progress' tip='Unloading or verification started but not completed' />
								<Dot color='#dc2626' label='Not verified' tip='Unloaded, but verification was skipped or never started' />
								<Dot color='#111827' label='Not unloaded' tip='Outlet has not started unloading' />
							</>
						) : (
							<>
								<Dot color='#16a34a' label='Scanned' tip='QR codes scanned at the kitchen before dispatch — time = first scan → last scan' />
								<Dot color='#f97316' label='Partly scanned' tip='Some outlets were dispatched without scanning' />
								<Dot color='#dc2626' label='Not scanned' tip='Dispatched without scanning QR codes' />
								<Dot color='#111827' label='Not dispatched' tip='Nothing dispatched yet for this item' />
							</>
						);
					}
					return (
						<>
							{isProcessDistribution && selectedTab === 'adherance' ? (
								// Adherence %: actual / ordered per order. Over 100% (more than ordered) is red too.
								<>
									<Dot color='#16a34a' label='95–100%' tip='Adherent: 95% to 100% of the ordered quantity' />
									<Dot color='#f97316' label='62–95%' tip='Short: 62% to 95% of the ordered quantity' />
									<Dot color='#dc2626' label='< 62% or > 100%' tip='Not adherent: under 62% of the ordered quantity, or more than was ordered' />
								</>
							) : (
								<Dot color='#16a34a' label='Within ±5%' tip='Dispatched quantity within ±5% of ordered' />
							)}
							{!(isProcessDistribution && selectedTab === 'adherance') && (
								<>
									<Dot color='#dc2626' label='Outside ±5%' tip='Dispatched quantity outside ±5% tolerance (over or under)' />
									<Dot color='#000000' label='Not dispatched' tip='Nothing dispatched yet for this row' />
								</>
							)}
						</>
					);
				case 'WASTAGE':
					return (
						<>
							<Dot color='#dc2626' label='Wastage' tip='Wastage / Production Loss recorded' />
							<Dot color='#e5e7eb' label='Zero' border='1px solid #cbd5e1' tip='No wastage recorded (value is 0)' />
						</>
					);
				default:
					return null;
			}
		})();

		return (
			<div className='flex items-center gap-2.5 text-[11px] font-medium flex-nowrap'>
				<div className='flex items-center gap-1.5 shrink-0'>
					<div className='flex bg-white rounded-md overflow-hidden relative items-center w-4 h-4 border border-gray-300 shrink-0'>
						<div className='z-10 absolute flex justify-center w-full h-full'>
							<div className='h-full w-[1px] bg-red-700 -rotate-45' />
						</div>
						<img src={noMachineIcon} alt='noMachineIcon' className='inline w-full h-full' />
					</div>
					<span className='text-gray-700'>Assumed value (Skipped by operator)</span>
				</div>

				{legendItems && (
					<div className='flex items-center gap-2.5 pl-2 ml-1 border-l border-slate-200 shrink-0'>
						{legendItems}
					</div>
				)}
			</div>
		);
	}, [selectedTab, parentTab, isFullScreen, tableTab, rawData, isProcessDistribution]);


	useEffect(() => {
		if (!isGridLoading && cacheTime && cacheTime !== 'Just now') {
			setHighlightCache(true);
			const timer = setTimeout(() => {
				setHighlightCache(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [cacheTime, isGridLoading]);

	useEffect(() => {
		if (!isStatsLoading && statsCacheTime && statsCacheTime !== 'Just now') {
			setHighlightStatsCache(true);
			const timer = setTimeout(() => {
				setHighlightStatsCache(false);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [statsCacheTime, isStatsLoading]);

	useEffect(() => {
		if (!getTableInfo?.()?.data?.length) return;
		setTimeout(() => {
			const tableEl = tableWithTabsRef.current;
			if (!tableEl) return;
			const pageHasScroll = document.documentElement.scrollHeight > document.documentElement.clientHeight;
			if (pageHasScroll) {
				tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	}, [getTableInfo?.()?.data]);

	return (
		<div className={isFullScreen ? 'fixed inset-0 z-[9999] bg-white overflow-auto p-4' : 'p-2'}>
			<style>
				{`
					@keyframes shake-tilt {
						0% { transform: rotate(0deg); }
						25% { transform: rotate(-5deg); }
						50% { transform: rotate(5deg); }
						75% { transform: rotate(-5deg); }
						100% { transform: rotate(0deg); }
					}
					.animate-shake-tilt {
						animation: shake-tilt 0.2s ease-in-out 1s 2;
					}
				`}
			</style>
			{isFullScreen && (
				<div className='flex items-center justify-between mb-3 pb-2 border-b border-gray-200'>
					<h2 className='font-bold text-lg text-gray-800'>Operations Summary — Full Screen</h2>
					<button
						onClick={() => setIsFullScreen(false)}
						className='flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-[12px] font-semibold transition-colors'
						title='Exit full screen (Esc)'
					>
						<Minimize2 size={14} />
						Exit Full Screen
						<span className='text-[10px] text-gray-400 ml-1 px-1 border border-gray-300 rounded'>Esc</span>
					</button>
				</div>
			)}
			<div className={isFullScreen ? 'hidden' : ''}>
				<ImagesFinder
					filtersInfo={filtersInfo}
					exportInfo={exportInfo}
					initialState={imagesFinderInitialState}
					defaultState={imagesFinderDefaultState}
					onSubmit={handleDataFetch}
					disableFutureDate
					timeTypeArray={dateTypeArr}
				/>
			</div>
			<>
				<div className='flex flex-col gap-1'>
					{/* Top row: Operations Summary title (left) + action buttons (right) */}
					<div className={`flex justify-between items-center ${isFullScreen ? 'hidden' : ''}`}>
						<h2 className='font-bold text-lg text-gray-800'>Operations Summary</h2>
						<div className='flex items-center gap-2'>
							<Button
								className="flex items-center gap-2 bg-white text-yellow-600 hover:bg-white font-semibold text-[14px] transition-colors duration-200"
								onClick={() => window.open(`${urls.EXTENSION}${urls.PRODUCTION_ANALYSIS}?tab=productGroupWise&dateRangeFilter=${encodeURIComponent(JSON.stringify(filters?.dateRange))}`, '_blank')}
							>
								<BarChart3 size={20} className='transition-transform duration-300  hover:scale-110 scale-100' />
								Production Analysis
								<SquareArrowOutUpRight size={18} />
							</Button>
							<Button
								className='flex items-center gap-2 w-fit bg-white text-yellow-600 hover:bg-white font-semibold text-[12px] transition-colors duration-200'
								onClick={() => window.open(`${urls.EXTENSION}${urls.PROCESS_FLOW_NEW}?&store=${filters?.fromPodFilter?.id ? JSON.stringify(filters?.fromPodFilter) : null}&dateOnly=${encodeURIComponent(JSON.stringify({
									date: filters?.dateRange?.to,
								}))}`, '_blank')}
							>
								<WorkflowIcon size={20} />
								<span>View Operation Flow</span>
								<SquareArrowOutUpRight size={18} />
							</Button>
							<Button
								className='flex items-center gap-2 w-fit bg-white text-yellow-600 hover:bg-white font-semibold text-[12px] transition-colors duration-200'
								onClick={() => setIsFullScreen(true)}
								title='Open table in full screen'
							>
								<Maximize2 size={18} />
								<span>Full Screen</span>
							</Button>
						</div>
					</div>
					{/* Operational Data view selector (Inventory / Process pill tabs + subtitle) */}
					<div className='mt-2 mb-1'>
						{OperationalDataTabsSection}
					</div>
					<div className={isFullScreen ? 'hidden' : ''}>{FilterSection}</div>

					<div className='flex flex-col gap-2 mb-2'>
						<div className='flex items-center justify-between'>
							<div className='scale-[0.95] origin-left'>
								{ParentTabsSection}
							</div>
							<div className='scale-[0.95] origin-right'>
								{DashboardTabsSelection}
							</div>
						</div>
						<div className='flex items-center justify-between'>
							<div className='flex items-center gap-4 scale-[0.95] origin-left'>
								{TabsSection}
								{subMealTabsSection}
							</div>
							<div className='flex items-center gap-3 scale-[0.95] origin-right'>
								{showOrderSourceTabs && (
									<div className='flex w-fit rounded bg-[#FDE6AD] border border-[#00000033] overflow-hidden'>
										{orderSourceTabs.map((ele) => {
											const isActive = orderSource === ele.id;
											return (
												<div
													key={ele.id}
													onClick={() => handleOrderSourceChange(ele.id)}
													title={`Show ${ele.label.toLowerCase()} orders`}
													className={`px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-colors
														${isActive ? 'bg-[#ffb703] text-gray-900' : 'text-gray-700 hover:bg-[#fde2a0]'}`}
												>
													{ele.label}
												</div>
											);
										})}
									</div>
								)}
								{/* Leftover adjustment doesn't apply to Process Data → Distribution */}
								{!(operationalDataView === 'processAnalysis' && parentTab?.value === 'DISPATCHED') && (
									<label className='flex items-center gap-1.5 cursor-pointer select-none'>
										<div className='relative'>
											<input
												type='checkbox'
												className='sr-only'
												checked={adjustLeftOver}
												onChange={(e) => handleAdjustLeftOverChange(e.target.checked)}
											/>
											<div className={`w-9 h-5 rounded-full transition-colors duration-200 ${adjustLeftOver ? 'bg-yellow-400' : 'bg-gray-300'}`} />
											<div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${adjustLeftOver ? 'translate-x-4' : 'translate-x-0'}`} />
										</div>
										<span className='text-[11px] font-medium text-gray-700'>Adjust Leftover</span>
									</label>
								)}
								{!isProcessDistribution && MealTabsSection}
							</div>
						</div>
					</div>

					{selectedBatches?.length > 0 && !isFullScreen && (
						<div className='flex flex-wrap items-center gap-2 p-1.5 bg-indigo-50/50 rounded-lg border border-indigo-100/50 mb-2'>
							<div className='flex items-center gap-2 pr-2 border-r border-indigo-200'>
								<Box size={14} className='text-indigo-600' />
								<span className='text-[10px] font-bold text-indigo-700 uppercase tracking-wider'>Selected:</span>
								<button
									onClick={() => setIsBatchPerformanceActive(true)}
									className="p-1 hover:bg-indigo-100 rounded-md transition-colors text-indigo-600"
									title="Edit Selection"
								>
									<Pencil size={12} />
								</button>
								<button
									onClick={() => setIsBatchesCollapsed((v) => !v)}
									className="flex items-center gap-1 text-[9px] font-bold text-indigo-600 hover:text-indigo-700 px-1.5 py-1 hover:bg-indigo-100 rounded-md transition-colors"
									title={isBatchesCollapsed ? 'Expand' : 'Collapse'}
								>
									{isBatchesCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
									{isBatchesCollapsed ? `Show (${selectedBatches.length})` : 'Hide'}
								</button>
							</div>
							{!isBatchesCollapsed && (
							<div className='flex flex-wrap gap-1.5'>
								{selectedBatches.map((batch) => (
									<div
										key={batch.grnItemBatchId}
										className='flex items-center gap-1.5 px-2.5 py-1 bg-white border border-indigo-200 rounded shadow-sm hover:border-indigo-400 transition-all'
									>
										<div className='flex flex-col'>
											<span className='text-[10px] font-bold text-gray-800 leading-tight'>
												{batch.productName || 'Product'}
												{batch.variantName ? ` (${batch.variantName})` : ''}
											</span>
											<div className='flex items-center gap-1.5'>
												<span className='text-[10px] font-medium text-indigo-600 tracking-tight'>{batch.batchName}</span>
												{batch.costingDate && (
													<span className='text-[9.5px] font-semibold text-gray-400 border-l border-gray-200 pl-1.5'>
														{moment(batch.costingDate).format('DD-MM-YYYY')} {batch.shiftName ? `(${batch.shiftName})` : ''}
													</span>
												)}
											</div>
										</div>
										<button
											onClick={() => {
												const updatedBatches = selectedBatches.filter(b => b.grnItemBatchId !== batch.grnItemBatchId);
												if (updatedBatches.length > 0) {
													handleBatchDataFetch(updatedBatches);
												} else {
													setSelectedBatches([]);
													setIsBatchPerformanceActive(true);
												}
											}}
											className='ml-1 text-gray-400 hover:text-red-500 transition-colors'
										>
											<span className='text-xs leading-none font-bold'>×</span>
										</button>
									</div>
								))}
								{selectedBatches.length > 0 && (
									<button
										onClick={() => {
											setSelectedBatches([]);
											setIsBatchPerformanceActive(true);
										}}
										className='text-[9px] font-bold text-red-500 hover:text-red-600 px-2 py-1 bg-red-50 hover:bg-red-100 rounded border border-red-100 transition-colors'
									>
										Clear All
									</button>
								)}
							</div>
							)}
						</div>
					)}

					<div className={isFullScreen ? 'hidden' : ''}>
						<StatsSection
							statsCards={statsSummary?.map((ele) => ({ ...ele, value: pairCardValue(ele) }))}
							isStatsLoading={isStatsLoading}
							statsCacheTime={statsCacheTime}
							cacheTime={cacheTime}
							highlightStatsCache={highlightStatsCache}
							onRefreshStats={refreshStats}
						/>
					</div>
					<div className={`flex w-full justify-between items-center ${isFullScreen ? 'hidden' : ''}`}>
						{(filters?.timeType?.value === 'scheduledAt' && selectedDashboard === 'plannedVsActual') ? (
							<div className='flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-amber-50/80 to-transparent border-l-4 border-amber-400/60 rounded-r-lg w-fit mt-[-4px] mb-4 transition-all duration-300 hover:from-amber-100/80'>
								<span className='text-[12px] text-gray-600 font-medium flex items-center gap-1.5'>
									<span className='w-1.5 h-1.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]' />
									Note: Data is being displayed based on its <span className='text-amber-700 font-bold'>Scheduled At</span> date context.
								</span>
							</div>
						) : <div />}
						{cacheTime && !isGridLoading && (
							<span
								onClick={() => {
									if (isGridLoading) return;
									refresh();
								}}
								className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm transition-all origin-center
									${isGridLoading ? 'cursor-not-allowed opacity-50 bg-orange-50 border-orange-200 text-orange-600' : 'cursor-pointer'}
									${highlightCache
										? 'bg-orange-200 border-orange-400 text-orange-800 ring-4 ring-orange-100 shadow-md animate-shake-tilt'
										: 'bg-orange-100 border-orange-200 text-orange-700 hover:bg-orange-200 hover:shadow-md duration-300'
									}`}
							>
								{isGridLoading ? <Loader2 size={12} className='animate-spin' /> : <RefreshCcw size={12} />}
								Last Synced {cacheTime}
							</span>
						)}
					</div>
				</div>
				<div className='relative [&_[role=tab]]:!px-3.5 [&_[role=tab]]:!py-1.5 [&_[role=tab]]:!text-[13px] [&_[role=tab][data-state=active]]:!h-9 [&_[role=tablist]]:!p-1'>
					<TableWithTabs
						tableWithTabsRef={tableWithTabsRef}
						tableInfo={getTableInfo()}
						tabInfo={getTabInfo()}
						height='auto'
						isLoading={isGridLoading}
						renderLegend={renderCustomLegend}
						note={note}
					/>
					{/* Not for Distribution — its live formats come from scan data, not the process data request flow */}
					{operationalDataView === 'processAnalysis' && parentTab?.value !== 'DISPATCHED' && !isGridLoading && (
						(() => {
							const isEmpty = !rawData
								|| (Array.isArray(rawData) && rawData.length === 0)
								|| (Array.isArray(rawData?.data) && rawData.data.length === 0);
							if (!isEmpty) return null;
							return (
								// In normal flow below the tabs: the table has no fixed height any more, so an
								// absolute overlay would spill up over the tabs / legend.
								<div className='flex items-center justify-center py-10'>
									<div className='bg-white border border-gray-200 rounded-xl shadow-sm px-8 py-6 max-w-md text-center flex flex-col items-center gap-3'>
										<div className='w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center'>
											<Database size={26} className='text-yellow-700' />
										</div>
										<div className='flex flex-col gap-1'>
											<h4 className='font-semibold text-[15px] text-gray-900'>No process data available</h4>
											<p className='text-[12.5px] text-gray-500 leading-snug'>
												We don't have process operational data for this view yet. Request it and the data team will prioritise it.
											</p>
										</div>
										<button
											type='button'
											onClick={() => setIsRequestDataModalOpen(true)}
											className='mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#ffb703] hover:bg-yellow-500 text-gray-900 text-[13px] font-semibold transition-colors'
										>
											<Database size={14} />
											Request Data
										</button>
									</div>
								</div>
							);
						})()
					)}
				</div>
				{isGridLoading && <LoadingSpinner isLoading={isGridLoading} />}
			</>
			<RequestOperationDataModal
				open={isRequestDataModalOpen}
				onClose={() => setIsRequestDataModalOpen(false)}
				context={{
					parentTab: parentTab?.header,
					selectedTab,
					tableTab,
					operationalDataView,
					dateRange: filters?.dateRange,
					store: filters?.fromPodFilter?.name ?? filters?.fromPodFilter?.id ?? null,
				}}
			/>
			<CustomModal />
			<BatchPerformanceHandler
				isActive={isBatchPerformanceActive}
				activeSelectAllOption
				filters={filters}
				shiftId={batchModalShiftId ?? active?.id}
				onShiftChange={setBatchModalShiftId}
				maxDateRangeDays={3}
				overrideDateRange={overrideDateRange}
				parentSelectedBatches={selectedBatches}
				onBatchSelect={(batch, date) => {
					handleBatchDataFetch(batch, date);
				}}
				onDateSelect={(from, to) => {
					setOverrideDateRange({ from, to });
				}}
				onCloseModal={(isConfirmed, currentBatches, currentDateRange) => {
					setIsBatchPerformanceActive(false);
					skipFetchRef.current = false; // Reset skip on close
					dispatch(modalActions.toggleCustomModal(false));

					const hasBatchChanged = JSON.stringify(currentBatches) !== JSON.stringify(selectedBatches);
					const hasDateChanged = currentDateRange && (
						!moment(currentDateRange.from).isSame(overrideDateRange?.from || filters?.dateRange?.from, 'day') ||
						!moment(currentDateRange.to).isSame(overrideDateRange?.to || filters?.dateRange?.to, 'day')
					);

					if (currentBatches && (hasBatchChanged || hasDateChanged)) {
						handleBatchDataFetch(currentBatches, currentDateRange);
					}

					const activeBatches = currentBatches || selectedBatches;
					const isEmpty = (Array.isArray(activeBatches) && activeBatches.length === 0) || !activeBatches;

					if (!isConfirmed && isEmpty && selectedDashboard === 'batchPerformance') {
						handleSelectDashboard('plannedVsActual');
					}
				}}
			/>
			{showScrollTop && (
				<button
					type='button'
					onClick={scrollToTop}
					title='Back to top'
					className='fixed bottom-6 right-6 z-[10000] w-10 h-10 rounded-full bg-[#ffb703] hover:bg-yellow-500 text-gray-900 shadow-lg flex items-center justify-center transition-colors'
				>
					<ArrowUp size={18} />
				</button>
			)}
		</div>
	);
};

export default AiFmsDynamicPage;
